import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCartCtx, useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import api from '../utils/api';
import { toast } from 'react-toastify';

// PAYMENT: no payment processor is wired up on the storefront yet. Checkout
// creates the order in a `pending` state and routes the customer to the
// order-success page. A high-risk payment processor will be integrated later —
// this submit handler is the seam to wire it in.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Active payment provider, decided by env:
//  - Wallet Plug (hosted redirect): create the order, get a checkout URL, leave
//    the SPA for Wallet Plug's page.
//  - NMI (Collect.js): route to the in-app /pay/:id card page.
//  - neither: keep today's behavior — order-success screen with follow-up
//    payment instructions.
const WALLETPLUG_ENABLED = String(process.env.REACT_APP_WALLETPLUG_ENABLED).toLowerCase() === 'true';
const NMI_ENABLED = Boolean(process.env.REACT_APP_NMI_TOKENIZATION_KEY);

// Flat surcharge for shipping outside the US — charged on top of standard
// shipping and never waived by VIP/coupon free shipping. MUST match the backend
// constant INTERNATIONAL_SHIPPING_SURCHARGE_CENTS in routes/orders.js.
const INTERNATIONAL_SHIPPING_SURCHARGE_CENTS = 5000; // $50.00

// Ship-to countries. Value is the ISO-2 code stored on the order; the backend
// treats anything other than "US" as international. US is pinned first.
const SHIP_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'CA', name: 'Canada' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MX', name: 'Mexico' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NO', name: 'Norway' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KR', name: 'South Korea' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TH', name: 'Thailand' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'VN', name: 'Vietnam' },
];

const EMPTY_ADDRESS = {
  recipientName: '', line1: '', line2: '', city: '', state: '', postalCode: '', phone: '', country: 'US',
};

// ─── Checkout draft (localStorage) ───────────────────────────────────────────
// So a customer who refreshes, closes the tab, or wants to "redo something"
// doesn't lose what they typed. Namespaced per account so a shared device never
// leaks one person's address into another's checkout. The durable, cross-device
// copy is saved to the account server-side on every successful order and
// prefilled here via user.savedShippingAddress.
const draftKey = (uid) => `aloha-checkout-draft:${uid || 'anon'}`;

function loadDraft(uid) {
  try {
    const raw = localStorage.getItem(draftKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function clearDraft(uid) {
  try { localStorage.removeItem(draftKey(uid)); } catch { /* ignore */ }
}

// A crypto-paying, non-member customer gets 15% off. Mirrors the authoritative
// server-side rule in routes/orders.js (VIP members excluded — their price is
// already final; the two 15% promos never stack).
const CRYPTO_DISCOUNT_RATE = 0.15;

export default function CheckoutPage() {
  const { items, total, retailTotal, retailUnit, clearCart, removeItem } = useCartCtx();
  const { isMember, user } = useAuthCtx();
  const { fmt } = useCurrency();
  const navigate = useNavigate();

  // Members pay VIP prices + ship free; everyone else pays retail. The backend
  // re-derives this authoritatively at checkout from the user's membership.
  const unitPrice = (i) => (isMember ? i.priceInCents : retailUnit(i));
  const subtotal  = isMember ? total : retailTotal;

  useEffect(() => {
    const stale = items.filter(i => !UUID_RE.test(String(i.variantId || '')));
    if (stale.length) {
      stale.forEach(i => removeItem(i.variantId));
      toast.warning(`Removed ${stale.length} outdated item(s) from your cart. Please re-add from the shop.`);
    }
  }, [items, removeItem]);

  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart', { replace: true });
    }
  }, [items.length, navigate]);

  const userId = user?.id;

  // Restore an in-progress draft (address / payment choice / coupon) so a lost
  // page or a "let me redo that" doesn't wipe what the customer typed. Read once
  // per mount.
  const [initialDraft] = useState(() => loadDraft(userId));

  const [address, setAddress] = useState(() => ({ ...EMPTY_ADDRESS, ...(initialDraft?.address || {}) }));

  // Payment method: 'crypto' (→ crypto settlement page, 15% off) or 'card'
  // (Debit/Credit → Wallet Plug hosted checkout). Crypto is the default since
  // it carries the discount.
  const [payMethod, setPayMethod] = useState(() => initialDraft?.payMethod === 'card' ? 'card' : 'crypto');

  // Whether we restored a real draft — if so, don't overwrite it with the
  // account-saved address on first load.
  const restoredDraftRef = useRef(Boolean(initialDraft?.address?.line1));

  // Prefill the recipient with the account holder's name (editable — gifts /
  // different ship-to are common). Only sets it once, when empty.
  useEffect(() => {
    const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    if (fullName) setAddress(prev => prev.recipientName ? prev : { ...prev, recipientName: fullName });
  }, [user]);

  // Prefill from the account's saved shipping address (set after a prior order),
  // but never clobber a restored draft or anything the customer has started
  // typing. Fires when /auth/me resolves with a saved address.
  useEffect(() => {
    const saved = user?.savedShippingAddress;
    if (!saved || restoredDraftRef.current) return;
    setAddress(prev => prev.line1 ? prev : {
      ...EMPTY_ADDRESS,
      ...saved,
      // Keep the name we already prefilled unless the saved copy has one.
      recipientName: saved.recipientName || prev.recipientName || '',
    });
  }, [user]);

  const [agreed, setAgreed] = useState(false);

  const [loading, setLoading] = useState(false);

  // ─── Store credit (cash back balance) ──────────────────────────────
  const [creditBalance, setCreditBalance] = useState(0); // available cents
  const [applyCredits, setApplyCredits]   = useState(false);
  useEffect(() => {
    api.get('/credit/summary')
      .then(res => setCreditBalance(res.data.availableCents || 0))
      .catch(() => { /* non-critical — checkout still works without credit */ });
  }, []);

  // Standard (domestic) shipping — VIP: free; else free over $200.
  const standardShipping = isMember ? 0 : (subtotal >= 20000 ? 0 : 699);
  // Non-US ship-to adds a flat surcharge on top (never waived by free shipping).
  const isInternational = (address.country || 'US').trim().toUpperCase() !== 'US';
  const intlSurcharge = isInternational ? INTERNATIONAL_SHIPPING_SURCHARGE_CENTS : 0;

  // ─── Coupon code ───────────────────────────────────────────────────
  const [couponInput, setCouponInput] = useState(() => initialDraft?.couponInput || '');
  const [coupon, setCoupon]           = useState(() => initialDraft?.coupon || null);   // { code, description, discountInCents, freeShipping }
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Persist the in-progress draft on every change. The coupon object is a
  // client cache only — the server re-validates it at checkout, so a stale one
  // is harmless. Cleared on a successful order.
  useEffect(() => {
    try {
      localStorage.setItem(draftKey(userId), JSON.stringify({ address, payMethod, couponInput, coupon }));
    } catch { /* quota / private mode — draft is best-effort */ }
  }, [userId, address, payMethod, couponInput, coupon]);

  const applyCouponCode = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setValidatingCoupon(true);
    setCouponError('');
    try {
      const { data } = await api.post('/orders/validate-coupon', {
        code,
        subtotalInCents: subtotal,
        shippingInCents: standardShipping,
        scope: 'one_time',
      });
      setCoupon(data);
      toast.success(`Coupon ${data.code} applied!`);
    } catch (err) {
      setCoupon(null);
      setCouponError(err.response?.data?.error || 'Could not apply coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const couponDiscount = coupon ? (coupon.freeShipping ? standardShipping : coupon.discountInCents) : 0;
  // Free-shipping coupon waives only the standard portion; the intl surcharge stays.
  const effectiveStandardShipping = coupon?.freeShipping ? 0 : standardShipping;
  const effectiveShipping = effectiveStandardShipping + intlSurcharge;
  // 15% off for paying in crypto (non-members only — VIP price is already final).
  // The server is authoritative; this is the checkout preview.
  const cryptoDiscount = (payMethod === 'crypto' && !isMember)
    ? Math.round(subtotal * CRYPTO_DISCOUNT_RATE)
    : 0;
  const totalBeforeCredit = Math.max(
    effectiveShipping,
    subtotal + effectiveShipping - (coupon?.freeShipping ? 0 : couponDiscount) - cryptoDiscount,
  );
  // Never redeem below the $1 processor floor (mirrors the server clamp). This is
  // a client-side preview; the backend re-derives the exact amount authoritatively.
  const creditApplied = applyCredits
    ? Math.min(creditBalance, Math.max(0, totalBeforeCredit - 100))
    : 0;
  const finalTotal = totalBeforeCredit - creditApplied;

  const proceedToPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/orders/checkout', {
        items: items.map(i => ({ variantId: i.variantId, quantity: i.quantity })),
        shippingAddress: {
          recipientName: (address.recipientName || '').trim(),
          line1:      (address.line1 || '').trim(),
          line2:      (address.line2 || '').trim(),
          city:       (address.city || '').trim(),
          state:      (address.state || '').trim(),
          postalCode: (address.postalCode || '').trim(),
          phone:      (address.phone || '').trim(),
          country:    (address.country || 'US').trim().toUpperCase().slice(0, 2),
        },
        couponCode: coupon?.code || undefined,
        paymentMethod: payMethod,
        applyCredits: creditApplied > 0,
        creditsToApply: creditApplied > 0 ? creditApplied : undefined,
      });
      // Order is created as `pending`. Route by the chosen payment method.
      const orderId = res.data.orderId;

      // The order is placed — safe to drop the saved draft now.
      clearDraft(userId);

      // Crypto → the black-and-gold settlement page (coin picker, QR, on-chain
      // confirmation). The 15% discount is already baked into the order total.
      if (payMethod === 'crypto') {
        clearCart();
        navigate(`/crypto-payment/${orderId}`);
        return;
      }

      // Debit/Credit → Wallet Plug hosted checkout.
      if (WALLETPLUG_ENABLED) {
        // Hosted redirect: ask the backend for Wallet Plug's checkout URL, then
        // leave the SPA. (If the order somehow already paid, skip to thank-you.)
        const { data } = await api.post(`/orders/${orderId}/walletplug/initiate`);
        if (data.alreadyPaid) {
          clearCart();
          navigate(`/thank-you/${orderId}`);
          return;
        }
        clearCart();
        window.location.href = data.paymentUrl;
        return;
      }

      // Card chosen but no online processor configured yet — NMI in-app card
      // page, or the no-processor "we'll follow up" fallback.
      clearCart();
      navigate(NMI_ENABLED ? `/pay/${orderId}` : `/order-success/${orderId}`);
      return;
    } catch (err) {
      const data = err.response?.data;
      const fieldMsg = Array.isArray(data?.fields) && data.fields.length
        ? data.fields.map(f => `${f.field}: ${f.message}`).join('; ')
        : null;
      toast.error(fieldMsg || data?.error || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main id="main-content" tabIndex={-1} className="flex-1 max-w-5xl mx-auto px-4 py-12 w-full">
        <h1 className="text-3xl font-bold mb-2">Checkout</h1>
        <p className="text-sm text-gray-500 mb-6">Prices in USD. We ship worldwide — orders outside the US add a flat {fmt(INTERNATIONAL_SHIPPING_SURCHARGE_CENTS)} international shipping surcharge.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

          {/* Left: address + payment method form */}
          <div>
              <form onSubmit={proceedToPayment} className="card space-y-4">
                <h2 className="font-bold text-xl">Shipping Address</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-gray-400 font-normal">(recipient)</span></label>
                  <input
                    value={address.recipientName}
                    onChange={e => setAddress(prev => ({ ...prev, recipientName: e.target.value }))}
                    required className="input-field" maxLength={120}
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input
                    value={address.line1}
                    onChange={e => setAddress(prev => ({ ...prev, line1: e.target.value }))}
                    required className="input-field" maxLength={200}
                    autoComplete="shipping address-line1" placeholder="123 Main St"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Apt / Suite / Unit <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    value={address.line2}
                    onChange={e => setAddress(prev => ({ ...prev, line2: e.target.value }))}
                    className="input-field" maxLength={120}
                    autoComplete="shipping address-line2" placeholder="Apt 4B"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      value={address.city}
                      onChange={e => setAddress(prev => ({ ...prev, city: e.target.value }))}
                      required className="input-field" maxLength={120}
                      autoComplete="shipping address-level2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State / Province {isInternational && <span className="text-gray-400 font-normal">(if applicable)</span>}
                    </label>
                    <input
                      value={address.state}
                      onChange={e => setAddress(prev => ({ ...prev, state: e.target.value }))}
                      required={!isInternational} className="input-field" maxLength={60}
                      autoComplete="shipping address-level1" placeholder={isInternational ? 'Region' : 'CA'}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP / Postal Code</label>
                    <input
                      value={address.postalCode}
                      onChange={e => setAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                      required className="input-field" maxLength={20}
                      autoComplete="shipping postal-code"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <select
                      value={address.country}
                      onChange={e => setAddress(prev => ({ ...prev, country: e.target.value }))}
                      className="input-field" autoComplete="shipping country"
                    >
                      {SHIP_COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {isInternational && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
                    <span aria-hidden="true">🌐</span>
                    <span>
                      International order — a flat {fmt(INTERNATIONAL_SHIPPING_SURCHARGE_CENTS)} shipping
                      surcharge is added on top of standard shipping. All prices and totals are in USD.
                    </span>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-gray-400 font-normal">(for delivery updates)</span></label>
                  <input
                    type="tel"
                    value={address.phone}
                    onChange={e => setAddress(prev => ({ ...prev, phone: e.target.value }))}
                    className="input-field" maxLength={40}
                    autoComplete="shipping tel" placeholder="(555) 555-5555"
                  />
                </div>

                <div className="pt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment method</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Crypto — carries the 15% discount */}
                    <label className={
                      'relative flex cursor-pointer flex-col rounded-xl border p-4 transition-colors ' +
                      (payMethod === 'crypto'
                        ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-300 dark:bg-amber-900/20 dark:border-amber-500'
                        : 'border-gray-200 hover:border-amber-300 dark:border-slate-700')
                    }>
                      {!isMember && (
                        <span className="absolute -top-2 right-3 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow">
                          Save 15%
                        </span>
                      )}
                      <div className="flex items-center gap-2">
                        <input
                          type="radio" name="payMethod" value="crypto"
                          checked={payMethod === 'crypto'}
                          onChange={() => setPayMethod('crypto')}
                          className="accent-amber-500"
                        />
                        <span className="font-semibold text-gray-800 dark:text-gray-100">Pay with Crypto</span>
                      </div>
                      <span className="mt-1 pl-6 text-xs text-gray-500">
                        BTC · ETH · LTC · USDT
                        {!isMember && cryptoDiscount > 0 && (
                          <span className="block font-medium text-amber-700 dark:text-amber-300">You save {fmt(cryptoDiscount)}</span>
                        )}
                      </span>
                    </label>

                    {/* Debit / Credit — Wallet Plug hosted checkout */}
                    <label className={
                      'flex cursor-pointer flex-col rounded-xl border p-4 transition-colors ' +
                      (payMethod === 'card'
                        ? 'border-tea-400 bg-tea-50 ring-1 ring-tea-300 dark:bg-slate-800 dark:border-slate-600'
                        : 'border-gray-200 hover:border-tea-300 dark:border-slate-700')
                    }>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio" name="payMethod" value="card"
                          checked={payMethod === 'card'}
                          onChange={() => setPayMethod('card')}
                          className="accent-tea-600"
                        />
                        <span className="font-semibold text-gray-800 dark:text-gray-100">Debit / Credit Card</span>
                      </div>
                      <span className="mt-1 pl-6 text-xs text-gray-500">Secure hosted card checkout</span>
                    </label>
                  </div>
                  <p className="flex items-center gap-1.5 text-xs font-medium text-tea-700 mt-2 dark:text-tea-300">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
                    </svg>
                    256-bit SSL Secured Payment • PCI DSS Compliant Safe Check Out
                  </p>
                </div>

                <label className="flex gap-3 items-start text-sm text-gray-700 pt-2">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={e => setAgreed(e.target.checked)}
                    className="mt-1"
                    required
                  />
                  <span>
                    I confirm I am a licensed researcher or laboratory provider and
                    acknowledge these products are <strong>for laboratory research
                    use only</strong> — not for human or veterinary consumption,
                    diagnosis, or treatment.
                  </span>
                </label>

                <button type="submit" disabled={loading || !agreed} className="btn-primary w-full py-3 disabled:opacity-50">
                  {loading
                    ? 'Placing Order...'
                    : payMethod === 'crypto' ? 'Continue to Crypto Payment' : 'Continue to Payment'}
                </button>
              </form>
          </div>

          {/* Right: order summary */}
          <div className="card h-fit">
            <h3 className="font-bold text-lg mb-4">Order Summary</h3>
            <div className="space-y-2 text-sm mb-4">
              {items.map(i => (
                <div key={i.variantId} className="flex justify-between">
                  <span className="text-gray-700">{i.productName} × {i.quantity}</span>
                  <span>{fmt(unitPrice(i) * i.quantity)}</span>
                </div>
              ))}
            </div>

            {/* Non-member VIP upsell */}
            {!isMember && retailTotal - total > 0 && (
              <button
                type="button"
                onClick={() => navigate('/membership')}
                className="w-full text-left rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-3 hover:border-amber-300 transition-colors"
              >
                <p className="text-sm font-bold text-amber-800">★ Members pay {fmt(total)} — save {fmt(retailTotal - total)}</p>
                <p className="text-xs text-amber-600 mt-0.5">Unlock VIP pricing + free shipping from $5/mo →</p>
              </button>
            )}

            {/* Coupon code — hidden for VIP members: their price is already final,
                so codes can't be combined with a membership. */}
            {isMember ? (
              <div className="border-t pt-4 mb-3">
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                  <span className="text-[10px] font-bold uppercase text-amber-600">VIP</span>
                  <span className="text-xs text-amber-800">
                    Your member price is already the lowest price — no code needed.
                  </span>
                </div>
              </div>
            ) : (
              <div className="border-t pt-4 mb-3">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Coupon code</div>
                {coupon ? (
                  <div className="bg-tea-50 border border-tea-300 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <div className="font-mono font-bold text-tea-700">{coupon.code}</div>
                      <div className="text-xs text-tea-700">
                        {coupon.freeShipping ? 'Free shipping' : `− ${fmt(coupon.discountInCents)} off`}
                      </div>
                    </div>
                    <button type="button" onClick={removeCoupon}
                      className="text-xs text-red-500 hover:text-red-700 font-medium">Remove</button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <input type="text" value={couponInput}
                        onChange={e => setCouponInput(e.target.value.toUpperCase())}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyCouponCode(); } }}
                        placeholder="Enter code"
                        className="flex-1 text-sm font-mono uppercase tracking-wider border border-gray-300 rounded-lg px-3 py-2"
                        maxLength={50} />
                      <button type="button" onClick={applyCouponCode} disabled={validatingCoupon || !couponInput.trim()}
                        className="text-sm font-semibold px-4 py-2 rounded-lg border border-tea-300 text-tea-700 hover:bg-tea-50 disabled:opacity-50">
                        {validatingCoupon ? '…' : 'Apply'}
                      </button>
                    </div>
                    {couponError && <p className="text-xs text-red-500 mt-1">{couponError}</p>}
                  </>
                )}
              </div>
            )}

            {/* Store credit (cash back) */}
            {creditBalance > 0 && (
              <div className="border-t pt-4 mb-3">
                <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-tea-200 bg-tea-50/60 p-3 hover:border-tea-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={applyCredits}
                    onChange={e => setApplyCredits(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-tea-700"
                  />
                  <span className="text-sm">
                    <span className="font-semibold text-tea-800">Apply store credit</span>
                    <span className="block text-xs text-tea-600 mt-0.5">
                      You have <strong>{fmt(creditBalance)}</strong> in cash-back credit available.
                    </span>
                  </span>
                </label>
              </div>
            )}

            <div className="border-t pt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Subtotal {isMember && <span className="text-[10px] font-bold uppercase text-amber-600">VIP</span>}</span><span>{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>{effectiveStandardShipping === 0 ? 'FREE' : fmt(effectiveStandardShipping)}</span>
              </div>
              {isInternational && (
                <div className="flex justify-between text-gray-700">
                  <span>International shipping</span>
                  <span>+ {fmt(intlSurcharge)}</span>
                </div>
              )}
              {coupon && !coupon.freeShipping && couponDiscount > 0 && (
                <div className="flex justify-between text-tea-700">
                  <span>Coupon ({coupon.code})</span>
                  <span>− {fmt(couponDiscount)}</span>
                </div>
              )}
              {cryptoDiscount > 0 && (
                <div className="flex justify-between text-amber-700 dark:text-amber-300">
                  <span>Crypto discount (15%)</span>
                  <span>− {fmt(cryptoDiscount)}</span>
                </div>
              )}
              {creditApplied > 0 && (
                <div className="flex justify-between text-tea-700">
                  <span>Store credit</span>
                  <span>− {fmt(creditApplied)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-base border-t pt-2 mt-2">
                <span>Total (USD)</span><span>{fmt(finalTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
